import {format, getDate, getMonth, getYear, isAfter} from 'date-fns';
import {APPROVAL_STATUS} from '../../../../constants';
import {TimeRegistration} from '../../../my-work-tab/my-timesheets-page/bottom-section/timesheets-time-registration-table/TimeRegistrationType';
import {trackEvent} from '../../../../tracking/amplitude/TrackingV2';
import {MODAL_TYPE, showModal} from '../modals/generic_modal_conductor';
import {createToast} from '../toasts/another-toast/toaster';
import Util from '../../../shared/util/util';
import UpdateTimeRegistration from '../../../../mutations/update_time_registration_mutation.modern';
import {IntlShape, MessageDescriptor} from 'react-intl';
import {FormatXMLElementFn, PrimitiveType} from 'intl-messageformat';

export type TimeRegistrationGraphQLFields = {
	// manually type the fields we need from the graphql query - currently we cannot mix and match typescript fragments with js fragments
	person?: {
		id: string;
	};
	task?: {
		estimateForecastMinutes: number | undefined;
		totalMinutesRegistered: number | undefined;
		id: string;
		billable: boolean;
		project?: {
			id: string;
		};
	};
	project?: {
		id: string;
		billable: boolean;
	};
	idleTimeId?: string;
	allocationId?: string;
	idleTime?: {
		id?: string;
		isInternalTime: boolean;
		name: string;
	};
	invoiced?: boolean;
	xeroInvoiceId?: string;
	lockedInPeriod?: boolean;
	noApprovalStatusChange?: string;
	approvalStatus?: string;
	billable: boolean;
	year: number;
	month: number;
	day: number;
	hiBobAllocationId?: string;
};

export type TimeRegistrationWithGraphQlFields = TimeRegistration & TimeRegistrationGraphQLFields;

export type TimeRegistrationNode = {
	node: TimeRegistrationWithGraphQlFields;
};
export function isLocked(timeReg: TimeRegistrationWithGraphQlFields): boolean {
	return (
		!!timeReg.invoiced ||
		!!timeReg.xeroInvoiceId ||
		!!timeReg.lockedInPeriod ||
		(!!timeReg.allocationId && !!timeReg.idleTime && !timeReg.idleTime.isInternalTime)
	);
}

export function getLockErrorMessage(
	dayOfInput: Date,
	lockedDate: Date,
	dayRegNodes: TimeRegistrationNode[],
	formatMessage: (descriptor: MessageDescriptor, values?: Record<string, PrimitiveType | FormatXMLElementFn>) => string,
	defaultMessage = '',
	excludeFromCompanyLockedPeriod: boolean
): string {
	if (!dayRegNodes?.length) {
		return defaultMessage;
	}

	const isDateLocked = lockedDate && !isAfter(dayOfInput, lockedDate);
	if (isDateLocked && !excludeFromCompanyLockedPeriod) {
		return formatMessage({id: 'time_lock.date_picker_info'}, {date: format(lockedDate, 'PPPP')});
	}

	const firstLockedTimeReg = dayRegNodes.find(timeReg => isLocked(timeReg.node))?.node;
	if (firstLockedTimeReg) {
		return firstLockedTimeReg.xeroInvoiceId
			? formatMessage({id: 'xero.cannot_edit_time_reg'})
			: firstLockedTimeReg.allocationId && !!firstLockedTimeReg.idleTime && !firstLockedTimeReg.idleTime.isInternalTime
			? formatMessage({id: 'timesheets.edit_time_off_as_allocation'})
			: firstLockedTimeReg.invoiced
			? formatMessage({id: 'invoice.cannot_edit_time_reg'})
			: firstLockedTimeReg.approvalStatus === APPROVAL_STATUS.APPROVED
			? formatMessage({id: 'approval.approved_time'})
			: firstLockedTimeReg.approvalStatus === APPROVAL_STATUS.SUBMITTED
			? formatMessage({id: 'approval.submitted_time'})
			: firstLockedTimeReg.lockedInPeriod
			? formatMessage({id: 'retainer.cannot_edit_time_reg'})
			: defaultMessage;
	}
	return defaultMessage;
}

export function getBambooHRLockErrorMessage(
	dayRegNodes: TimeRegistrationNode[],
	bambooHREnabled: boolean,
	lockMessage: string
): string {
	if (bambooHREnabled && dayRegNodes?.length) {
		const timeOffTimeReg = dayRegNodes.find(timeReg => timeReg.node.idleTime && !timeReg.node.idleTime.isInternalTime);
		if (timeOffTimeReg) {
			return lockMessage;
		}
	}
	return '';
}

type idleTime = {
	id: string;
	isInternalTime: boolean;
};

/**
 * Function to check if idle time is time off and if os, if it's blocked by bambooHR
 * @param idleTime
 * @param bambooHREnabled
 */
export function isTimeOffBlockedByBambooHR(idleTime: idleTime, bambooHREnabled: boolean): boolean {
	return bambooHREnabled && idleTime && !idleTime.isInternalTime;
}

export function isTimeOffBlockedByHiBob(idleTime: idleTime, hiBobEnabled: boolean): boolean {
	return hiBobEnabled && idleTime && !idleTime.isInternalTime;
}

function onTimeUpdateSuccess(result, intl) {
	if (
		result.updateTimeReg.errors &&
		result.updateTimeReg.errors.length === 1 &&
		result.updateTimeReg.errors[0] === 'TIME_REGISTRATION_IN_LOCKED_PERIOD'
	) {
		showModal({
			type: MODAL_TYPE.WARNING_DATE_LOCKED,
		});
	} else if (Util.checkForSageErrorAndShowModal(result.updateTimeReg.errors, false)) {
		return;
	} else {
		createToast({duration: 2500, message: intl.formatMessage({id: 'time_page.time_reg_updated'})});
	}
}

interface UpdateTimeRegistrationParameters {
	hours?: number;
	billableHours?: number;
	notes?: string;
	dayRegs: TimeRegistrationNode[]; // all time regs for the day
	timeRegId: string; // the id of the timereg we are updating
	roleId?: string;
	selectedPerson;
	skipApprovalValidation?: boolean;
	currentViewingDate: Date;
	intl: IntlShape;
	onSuccess: () => void;
}

export function updateTimeRegistration({
	hours,
	billableHours,
	notes,
	dayRegs,
	timeRegId,
	roleId,
	selectedPerson,
	currentViewingDate,
	skipApprovalValidation = false,
	intl,
	onSuccess,
}: UpdateTimeRegistrationParameters): void {
	trackEvent('Company Timesheets Hours Input', 'Changed', {
		inputHours: hours,
		timeRegPerson: selectedPerson.email,
		date: format(currentViewingDate, 'yyyy-mm-dd'),
	});

	const timeReg: TimeRegistrationWithGraphQlFields | undefined = dayRegs.find(timeReg => timeReg.node.id === timeRegId)?.node;
	if (!timeReg) {
		return;
	}

	if (isLocked(timeReg)) {
		return;
	}

	Util.CommitMutation(
		UpdateTimeRegistration,
		{
			source: 'Company-time-row-table',
			id: timeReg.id,
			day: getDate(currentViewingDate),
			month: getMonth(currentViewingDate) + 1,
			year: getYear(currentViewingDate),
			projectId: timeReg.task?.project?.id,
			taskId: timeReg.task?.id,
			idleTimeId: timeReg.idleTimeId,
			minutesRegistered: hours ? hours * 60 : 0,
			billableMinutesRegistered:
				billableHours === null
					? null
					: billableHours === undefined || isNaN(billableHours)
					? undefined
					: billableHours * 60,
			notes: notes ? notes : undefined,
			roleId: roleId,
			removeOthersForTheDay: false,
			skipApprovalValidation,
		},
		result => {
			onSuccess();
			onTimeUpdateSuccess(result, intl);
		}
	);
}
